// components/AdhesionFlow.tsx - CORRECTION: Transmission signature au MemberCard
import React, { useState, useRef } from 'react';
import PersonalInfoForm from './PersonalInfoForm';
import DocumentsForm from './DocumentsForm';
import MemberCard from './MemberCard';
import MembershipLevelSelector from './MembershipLevelSelector';
import { X, Check } from 'lucide-react';
import { AdhesionFormData, MemberData, ReferrerInfo, MembershipLevel, NipStatus } from '../../types/adhesion.types';
import { useMembership } from '../../hooks/useMembership';

interface AdhesionFlowProps {
    onClose?: () => void;
    onSuccess?: (memberData: MemberData) => void;
    referralCode?: string;
}

const AdhesionFlow: React.FC<AdhesionFlowProps> = ({ onClose, onSuccess, referralCode }) => {
    const [step, setStep] = useState(0);
    const [membershipLevel, setMembershipLevel] = useState<MembershipLevel>('full_member');
    const [nipStatus, setNipStatus] = useState<NipStatus>('unknown');

    // Ref pour le scroll automatique vers le haut
    const topRef = useRef<HTMLDivElement>(null);

    // Hook useMembership pour la vraie base de données
    const {
        createMembership,
        validateReferralCode,
        isLoading,
        error,
        clearError
    } = useMembership();

    const [formData, setFormData] = useState<AdhesionFormData>({
        firstName: '',
        lastName: '',
        phone: '',
        email: '',
        nip: '',
        dateOfBirth: '',
        placeOfBirth: '',
        country: 'Gabon',
        province: '',
        department: '',
        city: '',
        customCity: '',
        profession: '', // ✅ OBLIGATOIRE maintenant
        gender: '', // ✅ NOUVEAU CHAMP
        referredBy: referralCode || ''
    });

    const [selfie, setSelfie] = useState<string | null>(null);
    const [identityDocument, setIdentityDocument] = useState<string | null>(null);
    const [signature, setSignature] = useState<string | null>(null);
    const [member, setMember] = useState<MemberData | null>(null);
    const [referrerInfo, setReferrerInfo] = useState<ReferrerInfo | null>(null);

    // Fonction pour valider le code de parrainage et récupérer les infos
    const validateAndSetReferrerInfo = async (code: string) => {
        if (code && code.trim()) {
            const result = await validateReferralCode(code);

            if (result.valid && result.referrer) {
                setReferrerInfo(result.referrer);
            } else {
                setReferrerInfo(null);
            }
        }
    };

    // Vérifier le code de parrainage au chargement ET mettre à jour le formData
    React.useEffect(() => {
        if (referralCode) {

            // Mettre à jour le formData avec le code de parrainage
            setFormData(prev => ({ ...prev, referredBy: referralCode }));

            // Valider le code
            validateAndSetReferrerInfo(referralCode);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [referralCode]);

    // Fonction pour gérer les changements du code de parrainage dans le formulaire
    const handleReferralCodeChange = (code: string) => {
        setFormData(prev => ({ ...prev, referredBy: code }));

        // Valider le code si il a plus de 5 caractères
        if (code.trim().length > 5) {
            validateAndSetReferrerInfo(code.trim());
        } else if (code.trim().length === 0) {
            setReferrerInfo(null);
        }
    };

    const updateFormData = (data: Partial<AdhesionFormData>) => {
        setFormData(prev => ({ ...prev, ...data }));
    };

    // Fonction pour scroller vers le haut
    const scrollToTop = () => {
        if (topRef.current) {
            topRef.current.scrollIntoView({
                behavior: 'smooth',
                block: 'start'
            });
        } else {
            // Fallback si la ref n'est pas disponible
            window.scrollTo({
                top: 0,
                behavior: 'smooth'
            });
        }
    };

    // NOUVELLE fonction handleSubmit avec vraie base de données
    const handleSubmit = async () => {
        clearError();

        try {

            // Préparer les données pour le hook useMembership
            const membershipData = {
                firstName: formData.firstName.trim(),
                lastName: formData.lastName.trim(),
                phone: formData.phone.trim(),
                email: formData.email?.trim() || undefined,
                nip: formData.nip?.trim() || undefined,
                dateOfBirth: formData.dateOfBirth || undefined,
                placeOfBirth: formData.placeOfBirth?.trim() || undefined,
                country: formData.country.trim(),
                province: formData.province.trim(),
                department: formData.department?.trim() || undefined,
                city: formData.city.trim(),
                customCity: formData.customCity?.trim() || undefined, // ✅ AJOUT
                profession: formData.profession.trim(), // ✅ OBLIGATOIRE
                gender: formData.gender.trim(), // ✅ NOUVEAU CHAMP
                selfieBase64: selfie || undefined,
                identityDocumentBase64: identityDocument || undefined,
                signatureData: signature || undefined,
                referredBy: formData.referredBy?.trim() || undefined
            };


            // Créer le membre en base de données
            const newMember = await createMembership(membershipData);

            if (newMember) {

                // ✅ CORRECTION: Adapter les données pour le composant MemberCard avec signature
                const memberData: MemberData = {
                    id: newMember.id,
                    member_number: newMember.member_number,
                    first_name: newMember.first_name,
                    last_name: newMember.last_name,
                    department: newMember.department || undefined,
                    email: newMember.email || undefined,
                    phone: newMember.phone,
                    nip: newMember.nip || undefined,
                    city: newMember.city,
                    province: newMember.province,
                    country: newMember.country,
                    date_of_birth: formData.dateOfBirth || undefined,
                    place_of_birth: formData.placeOfBirth || undefined,
                    profession: newMember.profession || undefined,
                    gender: formData.gender || undefined, // ✅ NOUVEAU CHAMP
                    referral_code: newMember.referral_code,
                    created_at: newMember.created_at,
                    badges: newMember.badges || ['Pionnier'],
                    // ✅ CORRECTION CRUCIALE: Inclure signature_data dans memberData
                    signature_data: signature || undefined // ← AJOUTER la signature du flux d'adhésion
                };

                setMember(memberData);
                setStep(4);
                scrollToTop();

                if (onSuccess) {
                    onSuccess(memberData);
                }
            }
        } catch (err: any) {
            console.error('❌ Erreur lors de la création de l\'adhésion:', err);
            // L'erreur est déjà gérée par le hook useMembership
        }
    };

    return (
        <div ref={topRef} className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 pt-16">
            {/* Bouton fermer */}
            {onClose && (
                <button
                    onClick={onClose}
                    aria-label="Fermer le formulaire d'adhésion"
                    className="absolute top-4 right-4 z-50 w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-gray-100 focus:ring-4 focus:ring-blue-200 focus:outline-none transition-colors"
                >
                    <X className="w-5 h-5 text-gray-600" aria-hidden="true"/>
                </button>
            )}

            <div className="container mx-auto px-4 py-8">
                <div className="max-w-2xl mx-auto">
                    {/* Indicateur de progression */}
                    <div className="flex items-center justify-center space-x-2 mb-8">
                        {[0, 1, 2, 3, 4].map((stepNumber) => (
                            <div key={stepNumber} className="flex items-center">
                                <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold transition-all ${
                                    step > stepNumber
                                        ? 'bg-green-600 text-white shadow-lg'
                                        : step === stepNumber
                                            ? 'bg-blue-600 text-white shadow-lg animate-pulse'
                                            : 'bg-gray-200 text-gray-500'
                                }`}>
                                    {step > stepNumber ? <Check className="w-5 h-5" /> : stepNumber + 1}
                                </div>
                                {stepNumber < 4 && (
                                    <div className={`h-1 w-8 transition-all ${
                                        step > stepNumber ? 'bg-green-600' : 'bg-gray-200'
                                    }`}></div>
                                )}
                            </div>
                        ))}
                    </div>

                    {/* Affichage d'information de parrainage en haut */}
                    {referrerInfo && formData.referredBy && (
                        <div className="bg-gradient-to-r from-yellow-100 to-green-100 border border-yellow-200 rounded-lg p-6 mb-6">
                            <div className="flex items-start space-x-3">
                                <div className="w-10 h-10 bg-green-600 rounded-full flex items-center justify-center flex-shrink-0">
                                    <Check className="w-6 h-6 text-white" />
                                </div>
                                <div>
                                    <h3 className="text-green-800 font-semibold text-lg mb-2">
                                        🎉 Vous avez été invité(e) par un membre UDB !
                                    </h3>
                                    <div className="text-green-700">
                                        <p className="font-medium">
                                            Parrain : {referrerInfo.firstName} {referrerInfo.lastName}
                                        </p>
                                        <p className="text-sm">
                                            📍 {referrerInfo.city}, {referrerInfo.province}
                                        </p>
                                        <p className="text-sm mt-1">
                                            🎯 Code utilisé : <span className="font-mono bg-white px-2 py-1 rounded">{formData.referredBy}</span>
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Affichage des erreurs globales */}
                    {error && (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                            <div className="flex items-start space-x-2">
                                <X className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                                <div>
                                    <h3 className="text-red-800 font-medium">Erreur</h3>
                                    <p className="text-red-700 text-sm mt-1">{error}</p>
                                </div>
                                <button
                                    onClick={clearError}
                                    className="text-red-600 hover:text-red-800 ml-auto"
                                >
                                    <X className="w-4 h-4" />
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Étapes */}
                    {step === 0 && (
                        <MembershipLevelSelector
                            onLevelSelected={(level, nipStatus) => {
                                setMembershipLevel(level);
                                setNipStatus(nipStatus);
                                setStep(1);
                                scrollToTop();
                            }}
                        />
                    )}

                    {step === 1 && (
                        <PersonalInfoForm
                            formData={formData}
                            onFormDataChange={updateFormData}
                            onNext={() => {
                                setStep(2);
                                scrollToTop();
                            }}
                            onPrevious={() => {
                                setStep(0);
                                scrollToTop();
                            }}
                            referrerInfo={referrerInfo}
                            onReferralCodeChange={handleReferralCodeChange}
                            membershipLevel={membershipLevel}
                        />
                    )}

                    {step === 2 && (
                        <DocumentsForm
                            selfie={selfie}
                            identityDocument={identityDocument}
                            signature={signature}
                            onSelfieChange={setSelfie}
                            onDocumentChange={setIdentityDocument}
                            onSignatureChange={setSignature}
                            onNext={() => {
                                setStep(3);
                                scrollToTop();
                            }}
                            onPrevious={() => {
                                setStep(1);
                                scrollToTop();
                            }}
                        />
                    )}

                    {step === 3 && (
                        <div className="bg-white p-8 rounded-lg shadow-xl">
                            <h2 className="text-2xl font-bold text-gray-900 mb-6">Confirmation</h2>

                            {/* Récapitulatif avec TOUS les champs */}
                            <div className="bg-gray-50 p-6 rounded-xl mb-6">
                                <h3 className="font-semibold text-gray-900 mb-4">Récapitulatif de votre adhésion</h3>
                                <div className="space-y-2 text-sm">
                                    <p><span className="font-medium">Nom :</span> {formData.firstName} {formData.lastName}</p>
                                    <p><span className="font-medium">Téléphone :</span> {formData.phone}</p>
                                    {formData.email && <p><span className="font-medium">Email :</span> {formData.email}</p>}
                                    {formData.nip && <p><span className="font-medium">N° NIP :</span> {formData.nip}</p>}
                                    {formData.dateOfBirth && (
                                        <p><span className="font-medium">Date de naissance :</span> {new Date(formData.dateOfBirth).toLocaleDateString('fr-FR')}</p>
                                    )}
                                    {formData.placeOfBirth && (
                                        <p><span className="font-medium">Lieu de naissance :</span> {formData.placeOfBirth}</p>
                                    )}
                                    {/* ✅ NOUVEAU CHAMP SEXE */}
                                    {formData.gender && (
                                        <p><span className="font-medium">Sexe :</span> {formData.gender}</p>
                                    )}
                                    <p><span className="font-medium">Localisation :</span> {formData.city === 'autre' ? formData.customCity : formData.city}, {formData.province}, {formData.country}</p>
                                    {/* ✅ PROFESSION MAINTENANT OBLIGATOIRE */}
                                    {formData.profession && <p><span className="font-medium">Profession :</span> {formData.profession}</p>}
                                    {formData.referredBy && (
                                        <p><span className="font-medium">Code de parrainage :</span> {formData.referredBy}</p>
                                    )}
                                </div>
                            </div>

                            {/* Bloc d'information */}
                            <div className="bg-blue-50 border border-blue-200 p-6 rounded-xl mb-6">
                                <h4 className="font-semibold text-blue-600 mb-3">🎉 Vous allez recevoir :</h4>
                                <ul className="space-y-2 text-sm text-gray-700">
                                    <li className="flex items-center space-x-2">
                                        <Check className="w-4 h-4 text-green-600" />
                                        <span>Votre carte de membre virtuelle avec QR Code</span>
                                    </li>
                                    <li className="flex items-center space-x-2">
                                        <Check className="w-4 h-4 text-green-600" />
                                        <span>Votre formulaire d'adhésion signé électroniquement</span>
                                    </li>
                                    <li className="flex items-center space-x-2">
                                        <Check className="w-4 h-4 text-green-600" />
                                        <span>Badge "Pionnier"</span>
                                    </li>
                                    <li className="flex items-center space-x-2">
                                        <Check className="w-4 h-4 text-green-600" />
                                        <span>Votre code de parrainage unique</span>
                                    </li>
                                </ul>
                            </div>

                            <div className="flex space-x-4">
                                <button
                                    onClick={() => {
                                        setStep(2);
                                        scrollToTop();
                                    }}
                                    className="flex-1 bg-gray-300 text-gray-700 py-3 rounded-lg font-semibold hover:bg-gray-400 transition-colors"
                                    disabled={isLoading}
                                >
                                    Retour
                                </button>
                                <button
                                    onClick={handleSubmit}
                                    disabled={isLoading}
                                    className="flex-1 bg-gradient-to-r from-green-600 to-blue-600 text-white py-3 rounded-lg font-semibold hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                                >
                                    {isLoading ? (
                                        <>
                                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                                            <span>Création en cours...</span>
                                        </>
                                    ) : (
                                        <span>Confirmer l'adhésion</span>
                                    )}
                                </button>
                            </div>
                        </div>
                    )}

                    {step === 4 && member && (
                        <div className="space-y-8">
                            {/* Message de succès */}
                            <div className="bg-white p-8 rounded-lg shadow-xl text-center">
                                <div className="w-20 h-20 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-6">
                                    <Check className="w-10 h-10 text-white" />
                                </div>
                                <h2 className="text-3xl font-bold text-gray-900 mb-4">Bienvenue dans la famille UDB !</h2>
                                <p className="text-gray-600 text-lg mb-6">
                                    Votre adhésion a été confirmée avec succès. Vous faites maintenant partie de l'Union Démocratique des Bâtisseurs !
                                </p>
                                <div className="bg-green-50 border border-green-200 p-4 rounded-lg mb-6">
                                    <p className="text-green-700 text-sm mt-1">
                                        N° de membre : <strong>{member.member_number}</strong>
                                    </p>
                                </div>
                            </div>

                            {/* Carte de membre - UNIQUEMENT pour les membres avec NIP (full_member) */}
                            {nipStatus === 'has_nip' ? (
                                <MemberCard
                                    memberData={member}
                                    signatureData={signature}
                                    selfie={selfie}
                                    onClose={onClose}
                                />
                            ) : (
                                /* Message pour les membres sans NIP (sympathisant ou partial_member) */
                                <div className="bg-white p-8 rounded-lg shadow-xl">
                                    <div className="text-center mb-6">
                                        <h3 className="text-xl font-semibold text-gray-900 mb-4">
                                            {membershipLevel === 'sympathisant' ? 'Sympathisant enregistré' : 'Membre partiel enregistré'}
                                        </h3>
                                        <p className="text-gray-600 mb-4">
                                            Votre adhésion en tant que <strong>{membershipLevel === 'sympathisant' ? 'sympathisant' : 'membre partiel'}</strong> a été enregistrée avec succès.
                                        </p>
                                    </div>

                                    {/* Information sur la carte de membre */}
                                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
                                        <div className="flex items-start space-x-3">
                                            <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
                                                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                </svg>
                                            </div>
                                            <div>
                                                <h4 className="text-blue-800 font-semibold mb-2">
                                                    Carte de membre
                                                </h4>
                                                <p className="text-blue-700 text-sm">
                                                    La carte de membre officielle est réservée aux membres disposant d'un NIP (Numéro d'Identification Personnelle).
                                                    {membershipLevel === 'partial_member' && (
                                                        <span className="block mt-2">
                                                            Dès que vous obtiendrez votre NIP, vous pourrez mettre à jour votre profil et accéder à votre carte de membre.
                                                        </span>
                                                    )}
                                                </p>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Code de parrainage (disponible pour tous) */}
                                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 mb-6">
                                        <h4 className="text-yellow-800 font-semibold mb-2">Votre code de parrainage</h4>
                                        <p className="text-yellow-700 text-sm mb-3">
                                            Partagez ce code avec vos proches pour les inviter à rejoindre l'UDB :
                                        </p>
                                        <div className="bg-white border border-yellow-300 rounded-lg p-3 text-center">
                                            <span className="font-mono text-xl font-bold text-gray-900">{member.referral_code}</span>
                                        </div>
                                    </div>

                                    {/* Bouton fermer */}
                                    {onClose && (
                                        <div className="text-center">
                                            <button
                                                onClick={onClose}
                                                className="bg-blue-600 text-white px-8 py-3 rounded-lg hover:bg-blue-700 transition-colors font-semibold"
                                            >
                                                Fermer
                                            </button>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AdhesionFlow;
